import { Component, OnInit, inject } from '@angular/core';
import { ActivatedRoute, Router } from '@angular/router';
import { MatSnackBar } from '@angular/material/snack-bar';
import { BoRolesCheckService } from '../../../../components/rolesCheck/bo-roles-check.service';
import { AWSLambda } from 'src/app/AWSLambda.service';
import { EsimService } from '../../../esim/esim.service';

@Component({
  selector: 'app-bo-esim-gift-details',
  templateUrl: './bo-esim-gift-details.component.html',
  styleUrls: ['./bo-esim-gift-details.component.scss']
})
export class BOEsimGiftDetailsComponent implements OnInit {
  private router = inject(Router);
  private route = inject(ActivatedRoute);
  private snackBar = inject(MatSnackBar);
  private rolesCheck = inject(BoRolesCheckService);
  private lambda = inject(AWSLambda);
  private esimService = inject(EsimService);

  canAccess = false;
  loading = true;
  isSubmitting = false;

  // Usuario destino
  targetUserId: string = '';
  targetUserName: string = '';
  targetUserEmail: string = '';
  targetUserPhone: string = '';

  // Paquete
  packageId: string = '';
  packageData: any = null;

  // Detalles del paquete
  country: string = '';
  countryCode: string = '';
  countryImg: string = '';
  operator: string = '';
  planType: string = '';
  price: number = 0;
  title: string = '';
  data: string = '';
  validity: string = '';

  async ngOnInit() {
    this.canAccess = await this.rolesCheck.checkAccess();
    if (!this.canAccess) {
      return;
    }

    this.targetUserId = this.route.snapshot.paramMap.get('userId') || '';
    this.packageId = this.route.snapshot.paramMap.get('packageId') || '';

    if (!this.targetUserId || !this.packageId) {
      this.snackBar.open('Faltan parámetros necesarios', 'Cerrar', { duration: 5000 });
      this.router.navigate(['/backoffice/users']);
      return;
    }

    // Intentar obtener packageData del state
    const navigation = this.router.getCurrentNavigation();
    this.packageData = history.state?.packageData;

    // Si no viene por state, cargarlo desde la API
    if (!this.packageData) {
      await this.loadPackageDetails();
    } else {
      this.setPackageDetails(this.packageData);
    }

    await this.loadTargetUserDetails();
    this.loading = false;
  }

  async loadPackageDetails() {
    try {
      const apiPackages = await this.esimService.getfilteredPackagesbyID(this.packageId);

      if (!apiPackages) {
        this.snackBar.open('No se encontró el paquete', 'Cerrar', { duration: 5000 });
        this.goBack();
        return;
      }

      // Parsear la respuesta si viene como string
      const apiResponse = typeof apiPackages === 'string' ? JSON.parse(apiPackages) : apiPackages;

      if (!apiResponse || apiResponse.length === 0) {
        this.snackBar.open('No se encontró el paquete', 'Cerrar', { duration: 5000 });
        this.goBack();
        return;
      }

      const pkg = apiResponse[0];
      this.packageData = {
        id: pkg.pkg_id,
        country: pkg.pkg_country,
        country_code: pkg.pkg_country_code,
        countryimg: pkg.pkg_country_url,
        title: pkg.pkg_title,
        price: pkg.pkg_price,
        data: pkg.pkg_data,
        day: pkg.pkg_day,
        operador: [{
          operador: pkg.operador_title,
          plan_type: pkg.operador_plan_type,
          image: pkg.operador_url
        }]
      };

      this.setPackageDetails(this.packageData);
    } catch (error) {
      console.error('Error cargando paquete:', error);
      this.snackBar.open('Error al cargar el paquete', 'Cerrar', { duration: 5000 });
      this.goBack();
    }
  }

  setPackageDetails(pkg: any) {
    this.country = pkg.country || '';
    this.countryCode = pkg.country_code || '';
    this.countryImg = pkg.countryimg || pkg.operador?.[0]?.image || '';
    this.operator = pkg.operador?.[0]?.operador || '';
    this.planType = pkg.operador?.[0]?.plan_type || 'data';
    this.price = pkg.price || 0;
    this.title = pkg.title || '';
    this.data = pkg.data || '';
    this.validity = pkg.day ? `${pkg.day} ${pkg.day > 1 ? 'días' : 'día'}` : '';
  }

  async loadTargetUserDetails() {
    try {
      const data = await this.lambda.getLambdaFunction('lambda', '/get/userDetails', {
        queryStringParameters: { sub: this.targetUserId }
      });

      this.targetUserName = [data?.name, data?.middle_name].filter(Boolean).join(' ') || 'Usuario';
      this.targetUserEmail = data?.email || '';
      this.targetUserPhone = data?.phone_number || '';
    } catch (error) {
      console.error('Error cargando datos del usuario:', error);
    }
  }

  async assignEsim() {
    if (this.isSubmitting) return;

    this.isSubmitting = true;

    try {
      const orderData = {
        packageId: this.packageId,
        quantity: 1,
        country: this.country,
        country_code: this.countryCode,
        operatorTitle: this.operator,
        finalPrice: 0, // Siempre 0 para regalo
        promoCode: null,
        giftedTo: this.targetUserId
      };

      const response = await this.lambda.getLambdaFunction('lambda', '/add/Esim', {
        queryStringParameters: {
          user: this.targetUserId,
          phone: this.targetUserPhone,
          orderData: JSON.stringify(orderData)
        }
      });

      if (response?.ok) {
        this.snackBar.open('eSIM asignada correctamente', 'Cerrar', { duration: 3000 });

        // Redirigir al perfil del usuario
        setTimeout(() => {
          this.router.navigate(['/backoffice/users', this.targetUserId]);
        }, 1500);
      } else {
        console.error('Error al asignar eSIM:', response);
        this.snackBar.open(response?.mensaje || 'Error al asignar la eSIM', 'Cerrar', { duration: 5000 });
        this.isSubmitting = false;
      }
    } catch (error: any) {
      console.error('Error al asignar eSIM:', error);
      const errorMsg = error?.message || 'Error al asignar la eSIM';
      this.snackBar.open(errorMsg, 'Cerrar', { duration: 5000 });
      this.isSubmitting = false;
    }
  }

  goBack() {
    this.router.navigate(['/backoffice/users', this.targetUserId, 'gift-esim']);
  }

  capitalizeCountryName(country: string): string {
    if (!country) return '';
    return country
      .split('-')
      .map(word => word.charAt(0).toUpperCase() + word.slice(1))
      .join(' ');
  }
}
